import { ArrowUpRight, Target, Rocket, ShieldCheck } from "lucide-react";
import { ZOOMY } from "@/constants/zoomyIds";

const PILLARS = [
  {
    title: "Pre-qualified demand",
    sub: "The borrowers",
    icon: Target,
    body:
      "Hundreds of Zoomy renters already pay ₹250 a day to ride our EVs. They want to own — they just need financing. We bring you a qualified, intent-verified pipeline of EV buyers, ready to sign.",
  },
  {
    title: "Zero risk to Zoomy",
    sub: "The structure",
    icon: ShieldCheck,
    body:
      "The loan sits directly between you and the customer. Zoomy is not the borrower, co-borrower or guarantor. We originate, you underwrite — clean balance sheet on both sides.",
  },
  {
    title: "Asset-backed & serviced",
    sub: "The collateral",
    icon: Rocket,
    body:
      "Every financed EV is GPS-tracked, registered in the customer's name, and serviced through Zoomy's dealership network. Recovery, maintenance and resale support included.",
  },
];

export const PartnerSection = () => {
  const scrollContact = (e) => {
    e.preventDefault();
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="partner"
      data-testid={ZOOMY.partnerSection}
      className="relative py-24 sm:py-32 border-t border-white/5 bg-[#070707]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-6">
            <div className="font-mono text-xs uppercase tracking-[0.28em] text-lime-400 mb-5">
              // 04 — For financing partners
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]">
              Finance our <br />
              <span className="text-lime-400">customers&apos;</span> EVs. <br />
              Not us.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-4">
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Zoomy is opening up its rider base to financing partners. We have <span className="text-white font-semibold">100+ active EV riders</span> ready to upgrade from renting to owning over the next 12 months — and they need a lender.
            </p>
            <p className="mt-5 text-base text-slate-400 leading-relaxed">
              The loan is between <span className="text-white">you and the customer</span>. Zoomy doesn&apos;t guarantee the EMI, doesn&apos;t carry the asset on its books, and doesn&apos;t sit on your risk. We hand you pre-qualified borrowers; you write a clean retail-EV loan book.
            </p>
          </div>
        </div>

        {/* Pillar grid */}
        <div className="grid md:grid-cols-3 border border-white/10">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className={`relative p-8 sm:p-10 ${
                  i < PILLARS.length - 1 ? "md:border-r border-b md:border-b-0 border-white/10" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-lime-400">
                    {p.sub} · 0{i + 1}
                  </span>
                  <Icon className="w-5 h-5 text-lime-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">
                  {p.title}
                </h3>
                <p className="mt-5 text-sm sm:text-base text-slate-400 leading-relaxed">
                  {p.body}
                </p>
              </div>
            );
          })}
        </div>

        {/* Big pitch line */}
        <div className="mt-16 border border-lime-400 bg-lime-400/[0.04] p-8 sm:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-3xl">
            <div className="font-mono text-xs uppercase tracking-[0.28em] text-lime-400 mb-4">
              Borrower pipeline · Term sheet · 12-month plan available on request
            </div>
            <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight text-white">
              You write the loans. <span className="text-lime-400">We deliver the riders.</span>
            </p>
          </div>
          <a
            href="#contact"
            onClick={scrollContact}
            data-testid="partner-deck-cta"
            className="btn-lime shrink-0 inline-flex items-center gap-3 bg-lime-400 text-black font-bold uppercase tracking-wider text-sm px-7 py-4 hover:bg-lime-300 transition-colors"
          >
            Request partner deck
            <ArrowUpRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};
